/**
 * The keys of this tab, and who the viewer says they are.
 *
 * A workspace key is a secret, so it lives where the tab lives and no
 * longer: persona A in sessionStorage, personas B and C in memory unless the
 * viewer asks to keep them for the tab. The operator's `sbo_` key never
 * touches storage and is wiped after ten minutes without use. The user's own
 * Anthropic key, for the Director, is theirs to enter and remove: it lives in
 * sessionStorage only, for the tab, and Disconnect wipes it with the rest.
 * The profile (a name and a colour shown to peers) is not a secret and is
 * kept in localStorage. Every storage access is wrapped: a private window or
 * blocked site data costs memory, never the page.
 */

import type { CreateSession, Persona, Profile, SandboxInfo, SessionApi, Unsub } from './contracts';
import { redactText } from './spec/twins';

export const KEY_SLOT = 'schedule-bit.playground.key';
export const PROFILE_SLOT = 'schedule-bit.playground.profile';
/** The user's own Anthropic key: sessionStorage only, never localStorage. */
export const ANTHROPIC_SLOT = 'schedule-bit.playground.anthropic';
/** The sandbox this tab last had, without its key: sessionStorage, so a reload can say where it went. */
export const SANDBOX_SLOT = 'schedule-bit.playground.sandbox';
/** The operator key is dropped after this long without a touch. */
export const OPERATOR_IDLE_MS = 10 * 60_000;

const PERSONAS: readonly Persona[] = ['A', 'B', 'C'];
const slotOf = (p: Persona): string => (p === 'A' ? KEY_SLOT : `${KEY_SLOT}.${p}`);

export interface Timers { set(fn: () => void, ms: number): unknown; clear(id: unknown): void }

export interface SessionDeps {
  readonly now?: () => number;
  readonly timers?: Timers;
  /** A fresh tab id; random by default. */
  readonly tabId?: string;
}

const read = (s: Storage | null, k: string): string | null => {
  try { return s ? s.getItem(k) : null; } catch { return null; }
};
const write = (s: Storage | null, k: string, v: string | null): void => {
  try {
    if (!s) return;
    if (v === null) s.removeItem(k);
    else s.setItem(k, v);
  } catch {
    // Storage refused (quota, private mode): the value lives in memory for this page.
  }
};

/** The browser's storage, if this page may have it at all (reading the property can throw). */
const ambient = (): { session: Storage | null; local: Storage | null } => {
  const pick = (name: 'sessionStorage' | 'localStorage'): Storage | null => {
    try { return (globalThis as Record<string, unknown>)[name] as Storage ?? null; } catch { return null; }
  };
  return { session: pick('sessionStorage'), local: pick('localStorage') };
};

const HEX6 = /^#[0-9a-f]{6}$/;
/** A name peers will see: a key pasted into it by mistake goes no further than its prefix. */
const clampName = (name: string): string => Array.from(redactText(name.trim())).slice(0, 32).join('').trim();

/** A colour peers can tell apart, from a hue; lower-case `#rrggbb` as tickets require. */
export const colorOfHue = (hue: number): string => {
  // HSL at 65 % saturation, 55 % lightness: readable as a ring on both themes.
  const s = 0.65, l = 0.55;
  const k = (n: number) => (n + hue / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  return `#${[f(0), f(8), f(4)].map((x) => Math.round(x * 255).toString(16).padStart(2, '0')).join('')}`;
};

/** A profile as stored or typed, made safe to send: a 1–32 character name with no key in it, and a lower-case colour. */
export const profileOf = (value: unknown, fallback: Profile): Profile => {
  if (!value || typeof value !== 'object') return fallback;
  const v = value as { name?: unknown; color?: unknown };
  const name = typeof v.name === 'string' ? clampName(v.name) : '';
  const color = typeof v.color === 'string' ? v.color.trim().toLowerCase() : '';
  return { name: name || fallback.name, color: HEX6.test(color) ? color : fallback.color };
};

const randomId = (): string => {
  const bytes = new Uint8Array(4);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
};

export const normalizeBase = (base: string): string => base.trim().replace(/\/+$/, '');

export const createSession: CreateSession = (apiBase, storage) => makeSession(apiBase, storage, {});

export function makeSession(
  apiBase: string,
  storage: { session: Storage | null; local: Storage | null } | undefined,
  deps: SessionDeps,
): SessionApi {
  const { session, local } = storage ?? ambient();
  const now = deps.now ?? Date.now;
  const timers: Timers = deps.timers ?? { set: (fn, ms) => setTimeout(fn, ms), clear: (id) => clearTimeout(id as ReturnType<typeof setTimeout>) };
  // Fresh per page load, never restored: a reload restarts the Idempotency-Key
  // counter, and an old tab id would replay the keys it already spent.
  const tab = deps.tabId ?? randomId();
  const listeners = new Set<() => void>();
  const emit = () => { for (const fn of [...listeners]) { try { fn(); } catch { /* one listener's fault is its own */ } } };

  const keys = new Map<Persona, string>();
  const remembered = new Set<Persona>(['A']);
  for (const p of PERSONAS) {
    const stored = read(session, slotOf(p));
    if (stored) { keys.set(p, stored); remembered.add(p); }
  }

  let anthropic: string | null = read(session, ANTHROPIC_SLOT);
  // A sandbox's key lives in memory only: a reload drops it, and the marker says so.
  let sandbox: SandboxInfo | null = null;
  const sandboxOf = (raw: string | null): SandboxInfo | null => {
    try {
      const v = JSON.parse(raw ?? 'null') as Partial<SandboxInfo> | null;
      return v && typeof v.workspace === 'string' && typeof v.expiresAt === 'number' ? { workspace: v.workspace, expiresAt: v.expiresAt } : null;
    } catch { return null; }
  };

  let operator: { key: string; touched: number } | null = null;
  let operatorTimer: unknown = null;
  const dropOperator = () => {
    if (operatorTimer !== null) timers.clear(operatorTimer);
    operatorTimer = null;
    const had = operator !== null;
    operator = null;
    if (had) emit();
  };
  const armOperator = () => {
    if (operatorTimer !== null) timers.clear(operatorTimer);
    operatorTimer = operator ? timers.set(() => { operatorTimer = null; if (operator && now() - operator.touched >= OPERATOR_IDLE_MS) dropOperator(); else armOperator(); }, OPERATOR_IDLE_MS) : null;
  };

  // Any tab id gives a hue: a hex one reads as a number, anything else is hashed (a NaN hue made "#NaNNaN…", which tickets refuse).
  const hex = parseInt(tab.slice(0, 4), 16);
  const hue = (Number.isFinite(hex) ? hex : [...tab].reduce((h, c) => Math.imul(h ^ c.charCodeAt(0), 0x01000193) >>> 0, 0x811c9dc5)) % 360;
  const fallback: Profile = { name: `guest ${tab.slice(0, 3)}`, color: colorOfHue(hue) };
  let profile = profileOf((() => { try { return JSON.parse(read(local, PROFILE_SLOT) ?? 'null'); } catch { return null; } })(), fallback);

  return {
    apiBase: normalizeBase(apiBase),
    key: (persona = 'A') => keys.get(persona) ?? null,
    setKey(persona, key, remember) {
      const value = key?.trim() || null;
      if (value === null) keys.delete(persona);
      else keys.set(persona, value);
      // A key set for A is the tab's own again, not a sandbox's.
      if (persona === 'A') { sandbox = null; remembered.add('A'); }
      if (persona !== 'A') {
        if (remember === true) remembered.add(persona);
        if (remember === false) remembered.delete(persona);
      }
      // A is always the tab's; B and C only when asked, and forgotten from storage otherwise.
      write(session, slotOf(persona), remembered.has(persona) ? value : null);
      emit();
    },
    personas: () => PERSONAS.filter((p) => keys.has(p)),
    operatorKey() {
      if (operator && now() - operator.touched >= OPERATOR_IDLE_MS) dropOperator();
      return operator?.key ?? null;
    },
    setOperatorKey(key) {
      const value = key?.trim() || null;
      if (value === null) { dropOperator(); return; }
      operator = { key: value, touched: now() };
      armOperator();
      emit();
    },
    touchOperator() {
      if (!operator) return;
      if (now() - operator.touched >= OPERATOR_IDLE_MS) { dropOperator(); return; }
      operator.touched = now();
      armOperator();
    },
    sandbox: () => (keys.has('A') ? sandbox : null),
    setSandbox(key, info) {
      const value = key?.trim() || null;
      if (value === null || !info) {
        if (sandbox) keys.delete('A');
        sandbox = null;
      } else {
        keys.set('A', value);
        sandbox = info;
        write(session, SANDBOX_SLOT, JSON.stringify(info));
      }
      // Never in storage: a sandbox's key is this page's only.
      remembered.delete('A');
      write(session, KEY_SLOT, null);
      emit();
    },
    lastSandbox: () => sandboxOf(read(session, SANDBOX_SLOT)),
    anthropicKey: () => anthropic,
    setAnthropicKey(key) {
      anthropic = key?.trim() || null;
      write(session, ANTHROPIC_SLOT, anthropic);
      emit();
    },
    forgetAll() {
      for (const p of PERSONAS) write(session, slotOf(p), null);
      sandbox = null;
      write(session, SANDBOX_SLOT, null);
      keys.clear();
      remembered.clear();
      remembered.add('A');
      anthropic = null;
      write(session, ANTHROPIC_SLOT, null);
      if (operatorTimer !== null) timers.clear(operatorTimer);
      operatorTimer = null;
      operator = null;
      emit();
    },
    profile: () => profile,
    setProfile(p) {
      profile = profileOf(p, profile);
      write(local, PROFILE_SLOT, JSON.stringify(profile));
      emit();
    },
    tabId: () => tab,
    on(fn): Unsub {
      listeners.add(fn);
      return () => { listeners.delete(fn); };
    },
  };
}
